import { Preferences } from '@capacitor/preferences';

const localStorageAvailable = (() => {
  try {
    localStorage.setItem('Deckee_test', '1');
    localStorage.removeItem('Deckee_test');
  } catch (err) {
    return false;
  }
  return true;
})();

export const get = async (namespace, propertyName) => {
  const attemptSalvage = async (key) => {
    let { value } = await Preferences.get({ key });

    try {
      if (value) {
        value = JSON.parse(value);
      }
    } catch (err) {
      // There was an error parsing the value, nullify it in this case
      /* eslint-disable-next-line no-use-before-define */
      set(namespace, propertyName, null);
      value = null;
    }

    return value;
  };

  // If we're in the context of a browser
  if (typeof window !== 'undefined') {
    // Attempt to get the value that has been set permanently first.
    let value = await attemptSalvage(
      `Deckee_${namespace}_${propertyName}_permanent`,
    );
    if (!value) {
      // Otherwise try to get the normal value.
      value = await attemptSalvage(`Deckee_${namespace}_${propertyName}`);
    }
    return value;
  }
  return null;
};

const staticValues = {};
export const getStatic = (namespace, propertyName) => {
  const value =
    staticValues[`Deckee_${namespace}_${propertyName}_permanent`] ||
    staticValues[`Deckee_${namespace}_${propertyName}`];

  if (value) {
    return JSON.parse(value);
  }

  return value;
};

const setPersistentValue = async (key, parsedValue) => {
  // If we're in the context of a browser
  if (typeof window !== 'undefined') {
    if (parsedValue) {
      staticValues[key] = parsedValue;
      await Preferences.set({ key, value: parsedValue });
    }
  }
};

export const set = async (
  namespace,
  propertyName,
  value,
  permanent = false,
) => {
  if (namespace.indexOf('_') !== -1) {
    throw Error('Can not use underscores in namespace for local storage');
  }
  if (propertyName.indexOf('_') !== -1) {
    throw Error('Can not use underscores in propertyNames for local storage');
  }

  const key = `Deckee_${namespace}_${propertyName}${
    permanent ? '_permanent' : ''
  }`;

  let parsedValue;
  try {
    parsedValue = JSON.stringify(value);
  } catch (err) {
    // TODO: telemetry
    // console.error(`Error stringifying ${propertyName} with following value`, value);
    parsedValue = null;
  }

  await setPersistentValue(key, parsedValue);
};

export const remove = async (namespace, propertyName, permanent = false) => {
  // If we're in the context of a browser
  if (typeof window !== 'undefined') {
    const key = `Deckee_${namespace}_${propertyName}`;

    if (localStorageAvailable) {
      staticValues[key] = undefined;
      await Preferences.remove({ key });
      if (permanent) {
        staticValues[`${key}_permanent`] = undefined;
        await Preferences.remove({ key: `${key}_permanent` });
      }
    } else {
      document.cookie = `${key}=; max-age=0`;
      if (permanent) document.cookie = `${key}_permanent=; max-age=0`;
    }
  }
};

export const clearAll = (includingPermanent = false) => {
  const keys = localStorageAvailable
    ? Object.keys(localStorage)
    : document.cookie.split(';').map((kv) => kv.split('=')[0]);

  keys.forEach((key) => {
    if (key.indexOf('Deckee_') === 0) {
      const [, namespace, keyName] = key.split('_');
      remove(namespace, keyName, includingPermanent);
    }
  });
};

export const initialize = async () => {
  // Populate static values
  const { keys } = await Preferences.keys();
  const setStaticValuesPromises = keys.map(async (key) => {
    const { value } = await Preferences.get({ key });
    staticValues[key] = value;
  });
  await Promise.all(setStaticValuesPromises);

  // For each deckee_ key in localStorage, set it into the capacitor preferences storage if it doesn't already exist.
  const localStorageKeys = Object.keys(localStorage).filter(
    (key) => key.indexOf('Deckee_') === 0,
  );

  const localStorageKeysPromises = localStorageKeys.map(async (key) => {
    const value = localStorage.getItem(key);
    const { value: preferencesValue } = await Preferences.get({ key });

    if (!preferencesValue) {
      await setPersistentValue(key, value);
    }

    localStorage.removeItem(key);
  });

  await Promise.all(localStorageKeysPromises);
};
